import { useLayoutEffect, useState } from "react";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { Dialog, DialogActions, DialogContent, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsTextInput, Typography } from "@foodpilot/foods";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import {
    CreateActionPlanTemplateSchema,
    TEMPLATE_NAME_MAX_LENGTH,
    useCreateActionPlanTemplate,
    type CreateActionPlanTemplateInput,
} from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import {
    useTemplateCreationOptions,
    type TemplateSsqOption,
    type TemplateSsqVersion,
} from "src/api/client-api/actionPlanTemplates/useTemplateCreationOptions";
import { FoodsSelectField, FoodsServerSelectField } from "src/components/EntityForm/FoodsFields";
import { formatVersion } from "src/utils/formatting";

const SSQ_SEARCH_DELAY_MS = 300;

type CreateTemplateDialogProps = { onClose: () => void; onCreated: (id: string) => void };
type SectorOption = { id: number; name: string; ssqs: { ssqId: number; position: number }[] };

const sectorContainsSsq = (sector: SectorOption, ssqId: number) =>
    sector.ssqs.some((association) => association.ssqId === ssqId);

export const CreateTemplateDialog = (props: CreateTemplateDialogProps) => {
    const { onClose, onCreated } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const mutation = useCreateActionPlanTemplate();
    const ssqSearch = useDelayedSearch({ defaultDelay: SSQ_SEARCH_DELAY_MS });
    const sectorSearch = useDelayedSearch({ defaultDelay: SSQ_SEARCH_DELAY_MS });
    const [selectedSsq, setSelectedSsq] = useState<TemplateSsqOption | null>(null);
    const [selectedSsqVersion, setSelectedSsqVersion] = useState<TemplateSsqVersion | null>(null);
    const [selectedSector, setSelectedSector] = useState<SectorOption | null>(null);
    const [selectContainer, setSelectContainer] = useState<HTMLDivElement | null>(null);
    const [selectWidth, setSelectWidth] = useState<number>();
    const { ssqs, sectors, associations } = useTemplateCreationOptions(
        ssqSearch.delayedSearch,
        sectorSearch.delayedSearch,
        selectedSsq?.id,
        selectedSector?.id,
    );
    useLayoutEffect(() => {
        if (selectContainer === null) return;

        const updateWidth = () => {
            const width = selectContainer.getBoundingClientRect().width;
            setSelectWidth(width > 0 ? width : undefined);
        };
        updateWidth();

        if (typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(updateWidth);
        observer.observe(selectContainer);
        return () => observer.disconnect();
    }, [selectContainer]);
    const ssqSearchPending = ssqSearch.search !== ssqSearch.delayedSearch;
    const sectorSearchPending = sectorSearch.search !== sectorSearch.delayedSearch;
    const ssqOptions = ssqSearchPending ? [] : (ssqs.data ?? []);
    const sectorOptions = sectorSearchPending ? [] : (sectors.data ?? []);
    const versionOptions = [...(selectedSsq?.versions ?? [])]
        .filter((version) => version.status === 1)
        .sort((left, right) => right.versionMajor - left.versionMajor || right.versionMinor - left.versionMinor);
    const {
        control,
        handleSubmit,
        setError,
        setValue,
        resetField,
        formState: { isValid, isSubmitting, errors },
    } = useForm<CreateActionPlanTemplateInput>({
        resolver: zodResolver(CreateActionPlanTemplateSchema),
        mode: "onChange",
        defaultValues: { name: "" },
    });
    const busy = isSubmitting || mutation.isLoading;
    const close = () => {
        if (!busy) onClose();
    };
    const submit = handleSubmit(async (values) => {
        if (busy) return;
        try {
            const created = await mutation.mutateAsync(values);
            onCreated(created.id);
        } catch (error) {
            if (isAxiosError(error) && Array.isArray(error.response?.data?.violations)) {
                for (const violation of error.response.data.violations) {
                    if (
                        ["name", "ssqId", "ssqVersionId", "sectorId"].includes(violation.propertyPath) &&
                        typeof violation.message === "string"
                    ) {
                        setError(violation.propertyPath, { type: "server", message: violation.message });
                    }
                }
            }
        }
    });
    const nameLabel = t("settings.action_plan_templates.form.name", "Template name");
    const ssqLabel = t("settings.action_plan_templates.form.ssq", "Select the SSQ for this template");
    const sectorLabel = t(
        "settings.action_plan_templates.form.sector",
        "Select the industry sector this template applies to",
    );
    return (
        <Dialog open onClose={close} fullWidth maxWidth="md" aria-labelledby="create-template-title">
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={theme.spacing(2)}
                sx={{ p: theme.spacing(3), borderBottom: 1, borderColor: "divider" }}
            >
                <Typography id="create-template-title" variant="h5">
                    {t("settings.action_plan_templates.create", "Create a template")}
                </Typography>
                <Button
                    variant="text"
                    disabled={busy}
                    aria-label={t("settings.action_plan_templates.form.close", "Close")}
                    onClick={close}
                >
                    <FoodsIcon icon="close" size={1} />
                </Button>
            </Stack>
            <form onSubmit={submit} noValidate aria-busy={busy}>
                <DialogContent>
                    <Stack ref={setSelectContainer} gap={theme.spacing(3)} sx={{ py: theme.spacing(2) }}>
                        <Stack gap={theme.spacing(1)}>
                            <Typography component="label" htmlFor="template-name" variant="body-bold">
                                {nameLabel}*
                            </Typography>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <FoodsTextInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={busy}
                                        error={Boolean(errors.name)}
                                        ExtraInputProps={{
                                            id: "template-name",
                                            name: field.name,
                                            inputRef: field.ref,
                                            onBlur: field.onBlur,
                                            autoFocus: true,
                                            required: true,
                                            placeholder: t(
                                                "settings.action_plan_templates.form.name_placeholder",
                                                "Enter a template name…",
                                            ),
                                            inputProps: {
                                                maxLength: TEMPLATE_NAME_MAX_LENGTH,
                                                "aria-describedby": errors.name ? "template-name-error" : undefined,
                                            },
                                        }}
                                    />
                                )}
                            />
                            {errors.name && (
                                <Typography component="p" variant="body-regular" id="template-name-error" role="alert">
                                    {errors.name.message}
                                </Typography>
                            )}
                        </Stack>
                        <Controller
                            name="ssqId"
                            control={control}
                            render={({ field }) => (
                                <FoodsServerSelectField<TemplateSsqOption>
                                    appearance="product-v2"
                                    label={ssqLabel}
                                    required
                                    helperText={t(
                                        "settings.action_plan_templates.form.ssq_hint",
                                        "(cannot be changed later)",
                                    )}
                                    errorText={errors.ssqId?.message}
                                    options={ssqOptions}
                                    selectedOption={selectedSsq}
                                    onChange={(value) => {
                                        const currentVersion =
                                            value?.currentVersion?.status === 1 ? value.currentVersion : null;
                                        setSelectedSsq(value);
                                        setSelectedSsqVersion(currentVersion);
                                        field.onChange(value?.id);
                                        resetField("ssqVersionId");
                                        if (currentVersion) {
                                            setValue("ssqVersionId", currentVersion.id, { shouldValidate: true });
                                        }
                                        if (value && selectedSector && !sectorContainsSsq(selectedSector, value.id)) {
                                            setSelectedSector(null);
                                            resetField("sectorId");
                                        }
                                        ssqSearch.setSearch("");
                                        sectorSearch.setSearch("");
                                    }}
                                    getId={(item) => item.id}
                                    getName={(item) => item.title}
                                    placeholder={t(
                                        "settings.action_plan_templates.form.ssq_placeholder",
                                        "Select an SSQ",
                                    )}
                                    searchPlaceholder={t(
                                        "settings.action_plan_templates.form.ssq_search",
                                        "Search SSQs",
                                    )}
                                    emptyOptionsText={t(
                                        "settings.action_plan_templates.form.no_options",
                                        "No options available",
                                    )}
                                    noResultText={
                                        ssqs.isLoading || ssqSearchPending ?
                                            t("settings.action_plan_templates.form.loading_options", "Loading options…")
                                        :   t("settings.action_plan_templates.form.no_options", "No options available")
                                    }
                                    defaultValueText={t("component.select.defaultvalue", "Default value")}
                                    search={ssqSearch.search}
                                    setSearch={ssqSearch.setSearch}
                                    isSearchFound={() => true}
                                    disabled={busy}
                                    boxOptionsProps={selectWidth ? { width: selectWidth } : undefined}
                                    infiniteScroll={{
                                        hasNextPage: ssqs.hasNextPage ?? false,
                                        isFetchingNextPage: ssqs.isFetchingNextPage,
                                        onLoadMore: () => void ssqs.fetchNextPage(),
                                        loadingText: t(
                                            "settings.action_plan_templates.form.loading_options",
                                            "Loading options…",
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="ssqVersionId"
                            control={control}
                            render={({ field }) => (
                                <FoodsSelectField<TemplateSsqVersion>
                                    appearance="product-v2"
                                    label={t("settings.action_plan_templates.form.ssq_version", "SSQ version")}
                                    required
                                    helperText={t(
                                        "settings.action_plan_templates.form.ssq_hint",
                                        "(cannot be changed later)",
                                    )}
                                    errorText={errors.ssqVersionId?.message}
                                    options={versionOptions}
                                    selectedOption={selectedSsqVersion}
                                    onChange={(value) => {
                                        setSelectedSsqVersion(value);
                                        field.onChange(value?.id);
                                    }}
                                    getId={(item) => item.id}
                                    getName={formatVersion}
                                    placeholder={t("settings.action_plan_templates.form.ssq_version", "SSQ version")}
                                    searchPlaceholder={t(
                                        "settings.action_plan_templates.form.ssq_version_search",
                                        "Search SSQ versions",
                                    )}
                                    emptyOptionsText={t(
                                        "settings.action_plan_templates.form.no_options",
                                        "No options available",
                                    )}
                                    noResultText={t(
                                        "settings.action_plan_templates.form.no_options",
                                        "No options available",
                                    )}
                                    disabled={busy || selectedSsq === null}
                                    boxOptionsProps={selectWidth ? { width: selectWidth } : undefined}
                                />
                            )}
                        />
                        <Stack gap={theme.spacing(1)}>
                            <Controller
                                name="sectorId"
                                control={control}
                                render={({ field }) => (
                                    <FoodsServerSelectField<SectorOption>
                                        appearance="product-v2"
                                        label={sectorLabel}
                                        required
                                        errorText={errors.sectorId?.message}
                                        options={sectorOptions}
                                        selectedOption={selectedSector}
                                        onChange={(value) => {
                                            setSelectedSector(value);
                                            field.onChange(value?.id);
                                            if (value && selectedSsq && !sectorContainsSsq(value, selectedSsq.id)) {
                                                setSelectedSsq(null);
                                                setSelectedSsqVersion(null);
                                                resetField("ssqId");
                                                resetField("ssqVersionId");
                                                ssqSearch.setSearch("");
                                            }
                                            sectorSearch.setSearch("");
                                        }}
                                        getId={(item) => item.id}
                                        getName={(item) => item.name}
                                        placeholder={t(
                                            "settings.action_plan_templates.form.sector_placeholder",
                                            "Select an industry sector",
                                        )}
                                        searchPlaceholder={t(
                                            "settings.action_plan_templates.form.sector_search",
                                            "Search industry sectors",
                                        )}
                                        emptyOptionsText={
                                            sectorSearch.search.trim() ?
                                                t(
                                                    "settings.action_plan_templates.form.no_options",
                                                    "No options available",
                                                )
                                            :   t(
                                                    "settings.action_plan_templates.form.no_sectors_for_ssq",
                                                    "No industry sectors are attached to this SSQ.",
                                                )
                                        }
                                        noResultText={
                                            sectors.isLoading || sectorSearchPending ?
                                                t(
                                                    "settings.action_plan_templates.form.loading_options",
                                                    "Loading options…",
                                                )
                                            : sectorSearch.search.trim() ?
                                                t(
                                                    "settings.action_plan_templates.form.no_options",
                                                    "No options available",
                                                )
                                            :   t(
                                                    "settings.action_plan_templates.form.no_sectors_for_ssq",
                                                    "No industry sectors are attached to this SSQ.",
                                                )

                                        }
                                        defaultValueText={t("component.select.defaultvalue", "Default value")}
                                        search={sectorSearch.search}
                                        setSearch={sectorSearch.setSearch}
                                        isSearchFound={() => true}
                                        disabled={busy}
                                        boxOptionsProps={selectWidth ? { width: selectWidth } : undefined}
                                        infiniteScroll={{
                                            hasNextPage: sectors.hasNextPage ?? false,
                                            isFetchingNextPage: sectors.isFetchingNextPage,
                                            onLoadMore: () => void sectors.fetchNextPage(),
                                            loadingText: t(
                                                "settings.action_plan_templates.form.loading_options",
                                                "Loading options…",
                                            ),
                                        }}
                                    />
                                )}
                            />
                            {selectedSsq !== null &&
                                sectors.isSuccess &&
                                sectors.data?.length === 0 &&
                                !sectorSearchPending &&
                                sectorSearch.search.trim() === "" && (
                                    <Typography component="p" variant="body-regular" role="status">
                                        {t(
                                            "settings.action_plan_templates.form.no_sectors_for_ssq",
                                            "No industry sectors are attached to this SSQ.",
                                        )}
                                    </Typography>
                                )}
                        </Stack>
                        {(ssqs.isFetching || sectors.isLoading || associations.isLoading) && (
                            <Typography component="p" variant="body-regular" role="status">
                                {t("settings.action_plan_templates.form.loading_options", "Loading options…")}
                            </Typography>
                        )}
                        {(ssqs.isError || sectors.isError || associations.isError) && (
                            <Stack role="alert">
                                <Typography component="p" variant="body-regular">
                                    {t(
                                        "settings.action_plan_templates.form.options_failed",
                                        "Unable to load options. Please try again.",
                                    )}
                                </Typography>
                                <Button
                                    variant="text"
                                    onClick={() => {
                                        if (ssqs.isError) void ssqs.refetch();
                                        if (sectors.isError) void sectors.refetch();
                                        if (associations.isError) void associations.refetch();
                                    }}
                                >
                                    {t("settings.action_plan_templates.retry", "Try again")}
                                </Button>
                            </Stack>
                        )}
                        {mutation.isError && (
                            <Typography component="p" variant="body-regular" role="alert">
                                {t(
                                    "settings.action_plan_templates.form.create_failed",
                                    "Unable to create the template. Check the fields and try again.",
                                )}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{ justifyContent: "space-between", p: theme.spacing(3), borderTop: 1, borderColor: "divider" }}
                >
                    <Button
                        variant="text"
                        disabled={busy}
                        onClick={close}
                        endIcon={<FoodsIcon icon="close" size={1} />}
                    >
                        {t("settings.action_plan_templates.form.cancel", "Cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={
                            !isValid ||
                            busy ||
                            associations.isError ||
                            sectors.isError ||
                            selectedSsq === null ||
                            selectedSector === null ||
                            !sectorContainsSsq(selectedSector, selectedSsq.id)
                        }
                        startIcon={<FoodsIcon icon="check" size={1} />}
                    >
                        {t("settings.action_plan_templates.form.create", "Create")}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
